import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { WarningCircle, X } from '@phosphor-icons/react';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';
import { apiUpgradeRequest } from '@/api/services/account.service';
import { useState } from 'react';
import { toaster } from '@/components/ui/toaster';
import { fetchAPI } from '@/api/fetch';

type UsageType = 'agentic-interview' | 'copilot';

interface LimitExhaustedDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isFreePlan: boolean;
  usageType?: UsageType;
}

export function LimitExhaustedDialog({
  isOpen,
  onClose,
  isFreePlan,
  usageType = 'agentic-interview'
}: LimitExhaustedDialogProps) {
  const [isUpgrading, setIsUpgrading] = useState(false);

  const getMessages = () => {
    if (usageType === 'copilot') {
      return {
        freePlan: 'Unable to create a copilot session on free plan. Please upgrade your plan to access this feature.',
        exhausted:
          'You have exhausted your copilot meeting time limit. Please upgrade your plan to continue using this feature.'
      };
    }
    return {
      freePlan:
        'Unable to create an agentic interview session on free plan. Please upgrade your plan to access this feature.',
      exhausted:
        'You have exhausted your agentic interviews limit. Please upgrade your plan to continue using this feature.'
    };
  };

  const messages = getMessages();

  const handleUpgradeRequest = async () => {
    setIsUpgrading(true);
    try {
      const response = await fetchAPI<void>({
        request: () => apiUpgradeRequest()
      });

      if (response.success) {
        toaster.create({
          title: 'Upgrade request sent',
          description: 'We have received your upgrade request and will contact you soon.',
          type: 'success',
          duration: 5000
        });
        onClose();
      } else {
        toaster.create({
          title: 'Request failed',
          description: response.message || 'Failed to send upgrade request. Please try again.',
          type: 'error',
          duration: 5000
        });
      }
    } catch {
      toaster.create({
        title: 'Request failed',
        description: 'An error occurred while sending upgrade request. Please try again.',
        type: 'error',
        duration: 5000
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <DialogRoot
      role="alertdialog"
      placement="center"
      size="md"
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent borderRadius="12px">
        <DialogHeader>
          <DialogTitle>
            <Flex alignItems="center" gap="8px">
              <Box color="error.500">
                <WarningCircle size={32} weight="fill" />
              </Box>
              <Text fontSize="24px" fontWeight="bold">
                {isFreePlan ? 'Free Plan Limitation' : 'Usage Limit Reached'}
              </Text>
            </Flex>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Box borderBottom="1px solid" borderColor="neutrals.100" mb="24px" />
          <Text fontSize="16px" px="16px" color="neutrals.700">
            {isFreePlan ? messages.freePlan : messages.exhausted}
          </Text>
        </DialogBody>
        <DialogFooter gap="12px">
          <Button
            variant="outline"
            color="primary.900"
            borderColor="primary.900"
            fontSize="16px"
            borderRadius="8px"
            onClick={handleUpgradeRequest}
            loading={isUpgrading}
            disabled={isUpgrading}
          >
            Contact Us to Upgrade
          </Button>
          <DialogActionTrigger asChild>
            <Button
              bg="primary.900"
              w="120px"
              fontSize="16px"
              borderRadius="8px"
              color="neutrals.0"
              _hover={{ bg: 'primary.700' }}
              onClick={onClose}
            >
              OK
            </Button>
          </DialogActionTrigger>
        </DialogFooter>
        <DialogCloseTrigger m="12px">
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
